import type { Measure, Radian } from "@pdfking/math"
import { Asin, Atan2, Ceil, PI, Sin, Sqrt } from "@pdfking/math"

const MAX_CURVATURE = 0.95

// Maps t (0-1) to altitude factor (0-1) using ellipse distribution
export const CurvatureAltitude = (t: number): number => {
  const theta = ((t * PI) / 2) as Radian
  return Sin(theta)
}

// Calculate max altitude for given segment length and altitude segments
// The first segment has the largest dy (steepest part of ellipse)
// dy₁ = altitude * CurvatureAltitude(1/n)
// For valid ellipse path: dy₁ < segmentLength
// maxAltitude = segmentLength / CurvatureAltitude(1/n)
// Use 95% to ensure dx is never too small
export const CurvatureMaxAltitude = <T extends Measure>(
  segment_length: T,
  altitude_segments: number
): T => {
  const first_step = CurvatureAltitude(1 / altitude_segments)
  return ((MAX_CURVATURE * segment_length) / first_step) as T
}

// Calculate minimum segments needed to reach target altitude with given segment length
// Uses 0.95 factor for first segment dy, then finds n where maxAltitude(n) >= target
export const CurvatureMinSegments = <T extends Measure>(
  segment_length: T,
  target_altitude: T
): number => {
  // For n segments: maxAltitude = 0.95 * segmentLength / Sin(π/(2n))
  // We need: maxAltitude >= targetAltitude
  // So: 0.95 * segmentLength / Sin(π/(2n)) >= targetAltitude
  // Sin(π/(2n)) <= 0.95 * segmentLength / targetAltitude
  // π/(2n) <= arcsin(0.95 * segmentLength / targetAltitude)
  // n >= π / (2 * arcsin(0.95 * segmentLength / targetAltitude))
  const ratio = (MAX_CURVATURE * segment_length) / target_altitude
  if (ratio >= 1) return 1
  const angle = Asin(ratio)
  return Ceil(PI / (2 * angle))
}

// Yields angles relative to previous segment direction
// Each angle represents the turn needed from current direction to next segment
// If altitude exceeds max reach, clamps to max reachable altitude
export function* CurvatureAngles<T extends Measure>(
  segment_length: T,
  altitude: T,
  segment_count: number,
  initial_rotation: Radian = (PI / 2) as Radian
): Generator<Radian> {
  // Clamp altitude to max reachable
  const max_altitude = CurvatureMaxAltitude(segment_length, segment_count)
  const effective_altitude = altitude > max_altitude ? max_altitude : altitude

  let prev_angle = initial_rotation

  for (let i = 1; i <= segment_count; i++) {
    const prev_t = (i - 1) / segment_count
    const curr_t = i / segment_count
    const prev_y = effective_altitude * CurvatureAltitude(prev_t)
    const curr_y = effective_altitude * CurvatureAltitude(curr_t)
    const dy = curr_y - prev_y
    const dx_squared = segment_length ** 2 - dy ** 2
    const dx = Sqrt(dx_squared > 0 ? dx_squared : 0)
    const curr_angle = Atan2(dy, dx)
    yield (curr_angle - prev_angle) as Radian
    prev_angle = curr_angle
  }

  // Final angle to flatten out (reach 0 degrees / horizontal)
  yield -prev_angle as Radian
}
